import React, { useEffect, useState } from "react";
import { MessageSquarePlus, Send, Clock, CheckCircle2, AlertCircle, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PRIORITY_COLORS = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-amber-50 text-amber-700",
  high: "bg-red-50 text-red-600",
};

const STATUS_COLORS = {
  open: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-green-50 text-green-700 border-green-200",
  closed: "bg-slate-100 text-slate-500 border-slate-200",
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';

export default function Complaints() {
  const [complaints, setComplaints] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const [form, setForm] = useState({
    category: "", subject: "", message: "", priority: "medium",
  });

  const load = () => {
    const params = new URLSearchParams({ page, limit: 15 });
    if (statusFilter !== "all") params.set("status", statusFilter);
    api.get(`/complaints?${params}`).then(r => {
      setComplaints(r.data.items || []);
      setTotal(r.data.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/complaints/categories").then(r => {
      setCategories(r.data);
      setForm(f => ({ ...f, category: r.data[0] || "" }));
    });
  }, []);

  useEffect(() => { load(); }, [page, statusFilter]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields"); return;
    }
    try {
      await api.post("/complaints", form);
      toast.success("Complaint submitted successfully!");
      setModal(false);
      setForm({ category: categories[0] || "", subject: "", message: "", priority: "medium" });
      setPage(1);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to submit"); }
  };

  const sendReply = async (complaintId) => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      await api.post(`/complaints/${complaintId}/reply`, { message: replyText });
      toast.success("Reply sent!");
      setReplyText("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setReplying(false); }
  };

  if (loading) return <div className="text-slate-400 pt-8 text-center">Loading…</div>;

  return (
    <div className="space-y-4 py-4">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-xl text-brand-nav">My Complaints</h2>
        <button onClick={() => setModal(true)}
          className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primaryHover text-white text-sm font-semibold px-4 py-2.5 rounded-lg shadow-sm">
          <MessageSquarePlus className="w-4 h-4" /> New Complaint
        </button>
      </div>

      {/* Status filter pills */}
      <div className="flex gap-2 flex-wrap">
        {["all", "open", "in_progress", "resolved", "closed"].map(s => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${
              statusFilter === s ? "bg-brand-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}>
            {s === "all" ? "All" : s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {/* Complaints list */}
      {complaints.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
          <MessageSquarePlus className="w-8 h-8 mx-auto mb-3 text-slate-300" />
          <p>No complaints yet. Submit one if you need help!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {complaints.map((c) => {
            const isOpen = expanded === c.id;
            return (
              <div key={c.id} className={`bg-white rounded-xl border transition-colors ${isOpen ? 'border-brand-primary/30 shadow-sm' : 'border-slate-200'}`}>
                <div className="p-4 cursor-pointer" onClick={() => setExpanded(isOpen ? null : c.id)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-mono text-slate-400">{c.ticket_no}</span>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${STATUS_COLORS[c.status] || ''}`}>
                          {c.status?.replace(/_/g, ' ')}
                        </span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${PRIORITY_COLORS[c.priority] || ''}`}>
                          {c.priority}
                        </span>
                      </div>
                      <p className="font-semibold text-brand-nav text-sm mt-1">{c.subject}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{c.category} · {fmtDate(c.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {c.replies?.length > 0 && (
                        <span className="text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded font-semibold">
                          {c.replies.length} {c.replies.length === 1 ? 'reply' : 'replies'}
                        </span>
                      )}
                      {isOpen ? <ChevronUp className="w-4 h-4 text-brand-primary" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>
                </div>

                {isOpen && (
                  <div className="px-4 pb-4 border-t border-slate-100">
                    {/* Original message */}
                    <div className="mt-3 bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-400 mb-1">Your message:</p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.message}</p>
                    </div>

                    {/* Replies */}
                    {c.replies?.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Replies</p>
                        {c.replies.map((r) => (
                          <div key={r.id} className={`rounded-lg p-3 text-sm ${
                            r.by_role === 'admin' || r.by_role === 'superadmin'
                              ? 'bg-indigo-50 border border-indigo-100'
                              : 'bg-slate-50'
                          }`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-slate-600">
                                {r.by_name || r.by_role} <span className="text-slate-400 font-normal">({r.by_role})</span>
                              </span>
                              <span className="text-[10px] text-slate-400">{fmtDate(r.created_at)}</span>
                            </div>
                            <p className="text-slate-700 whitespace-pre-wrap">{r.message}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reply box */}
                    {c.status !== 'closed' && (
                      <div className="mt-3 flex gap-2">
                        <input
                          placeholder="Write a reply…"
                          value={expanded === c.id ? replyText : ""}
                          onChange={e => setReplyText(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && sendReply(c.id)}
                          className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                        />
                        <button onClick={() => sendReply(c.id)} disabled={replying || !replyText.trim()}
                          className="bg-brand-primary hover:bg-brand-primaryHover text-white px-3 py-2 rounded-lg disabled:opacity-50 transition">
                          {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > 15 && (
        <div className="flex justify-center gap-2 pt-2">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            className="px-3 py-1 rounded border border-slate-200 text-xs disabled:opacity-40">Prev</button>
          <span className="text-xs text-slate-500 py-1">Page {page} of {Math.ceil(total / 15)}</span>
          <button disabled={page >= Math.ceil(total / 15)} onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 rounded border border-slate-200 text-xs disabled:opacity-40">Next</button>
        </div>
      )}

      {/* New Complaint Modal */}
      {modal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setModal(false)}>
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
            <h3 className="font-heading font-bold text-xl text-brand-nav">New Complaint</h3>
            <p className="text-xs text-slate-400 mt-1">Describe your issue and we'll get back to you.</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Category *</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm">
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Subject *</label>
                <input required value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                  placeholder="Brief summary of your issue"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Message *</label>
                <textarea required rows={4} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="Describe your issue in detail…"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm resize-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Priority</label>
                <div className="flex gap-2">
                  {["low", "medium", "high"].map(p => (
                    <button key={p} type="button" onClick={() => setForm(f => ({ ...f, priority: p }))}
                      className={`flex-1 py-2 rounded-lg text-xs font-semibold capitalize transition-colors border ${
                        form.priority === p
                          ? p === 'high' ? 'bg-red-500 text-white border-red-500'
                            : p === 'medium' ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-slate-600 text-white border-slate-600'
                          : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}>{p}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={() => setModal(false)} className="flex-1 border border-slate-200 text-slate-600 rounded-lg px-4 py-2.5 font-semibold hover:bg-slate-50">Cancel</button>
              <button type="submit" className="flex-1 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-lg px-4 py-2.5 font-semibold">Submit</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
